import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { KeenIcon, CustomOption, CustomSingleValue, LazyFormikSelect, Alert } from '@/components';
import { toAbsoluteUrl } from '@/utils';
import { useLazyOptions } from '@/hooks';
import userService from '@/services/setting/userService';
import { Formik, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import contactService from '@/services/contact/contactService';
import matterService from '@/services/matter/matterService';
import { ArrowDownToLine } from 'lucide-react';
import mapFieldService from '../../services/map-fields/mapFieldService';
import { toast } from 'sonner';

/** helpers */
const normalize = (s) =>
    (s ?? '')
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, '_');

const DropZoneModal = forwardRef(({
    show,
    onClose,
    onConfirm,
    uploadedFile,
    fileError,
    fileData,
    title,
    loading,
    sampleFile,
    getRootProps,
    getInputProps,
    isDragActive,
    error,
    removeFile,
    serverErrors,
    module,
    enableExternalImport = false,
    sourceFields,
    onSourceFieldsLoaded,
    selectedSource,
    setSelectedSource,
    buttonLabel = `Import ${module}`,
    uniqueConfirmation
}, formikRef) => {
    console.log('enableExternalImport', enableExternalImport);

    if (!show) return null;
    const [userData, setuserData] = useState([]);
    const [dataLoading, setDataLoading] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [sourcesData, setSourcesData] = useState(null)

    const getAllSources = async () => {
        try {
            const { data, status } = await mapFieldService.getAllSources();
            if (status) {
                const Data = data.filter(f => f.type === module).map(template => ({
                    label: template.system_name,
                    value: template.id.toString(),
                    id: template.id.toString(),
                }));
                setSourcesData(Data)
                return Data
            }
        } catch (error) {
            console.error('Error', error)
            toast.error('Failed to fetch Templates.', {
                description: 'An error occurred while fetching templates. Please try again later.',

            });
        }
    }
    const {
        data: sourceOptions,
        loading: sourceLoading,
        load: loadSource
    } = useLazyOptions(getAllSources);

    const getSingleSource = async (id) => {
        try {
            const { data, status } = await mapFieldService.getSingleSource(id);
            if (status) {
                const parsedMapping = JSON.parse(data.mapping || '{}');

                // Convert to normalized list of headers expected from Excel
                // Example: ["name", "contact", "company_name"]
                const expectedHeaders = Object.values(parsedMapping);


                const fields = {
                    id: data.id,
                    name: data.system_name,
                    expectedHeaders,
                    mapping: parsedMapping,
                    required_fields: data.required_fields || []
                };
                setSourcesData(fields);
                onSourceFieldsLoaded(fields);
                return {
                    id: data.id,
                    name: data.system_name,
                    expectedHeaders,
                    mapping: parsedMapping,
                    required_fields: data.required_fields || []
                };
            }
        } catch (error) {
            console.error('Error', error)
            toast.error('Failed to fetch Templates.', {
                description: 'An error occurred while fetching templates. Please try again later.',

            });
        }
    }

    // useEffect(() => {
    //     resetUser();
    // }, [fileData]);

    const handleDownloadSampleFile = async () => {
        setDownloadLoading(true);
        try {
            const response =
                module === 'contact'
                    ? await contactService.contactSampleFile()
                    : await matterService.matterSampleFile();

            const blob = new Blob([response.data], {
                type: response.headers['content-type'] || 'application/octet-stream',
            });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            const contentDisposition = response.headers['content-disposition'];
            const filename = contentDisposition
                ? contentDisposition.split('filename=')[1]?.replace(/"/g, '') || 'sample-file.xlsx'
                : 'sample-file.xlsx';

            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading sample file:', error);
        } finally {
            setDownloadLoading(false);
        }
    };

    /* ================== Error UX: group & jump ================== */

    // >>> CHANGE: normalize server row → table data index (handles matter offset)
    const toDataIndex = (serverRow) => {
        // For 'matter', first data row is serverRow=2; for 'contact', it's 1
        const offset = module === 'matter' ? -1 : 0;
        const idx = serverRow + offset;
        return idx < 1 ? 1 : idx; // guard
    };

    // Map headers to column index
    const headerIndexMap = useMemo(() => {
        const map = {};
        const headers = fileData?.[0] || [];
        headers.forEach((h, i) => {
            const k = normalize(h);
            map[k] = i;
            // alias for *_id vs "x id"
            map[k.replace(/_id$/, ' id')] = i;
        });
        return map;
    }, [fileData]);

    // Group server errors by row
    const groupedErrors = useMemo(() => {
        const rows = new Map();
        (serverErrors || []).forEach(e => {
            const r = e.row; // server row
            if (!rows.has(r)) rows.set(r, { rowNumber: r, items: [] });
            rows.get(r).items.push({
                attribute: e.attribute,
                errors: e.errors || [],
                value: e.values?.[e.attribute],
            });
        });
        return Array.from(rows.values()).sort((a, b) => a.rowNumber - b.rowNumber);
    }, [serverErrors]);

    const highlightTimerRef = useRef(null);
    const doHighlight = (el) => {
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        el.classList.add('text-danger', 'bg-danger-clarity');
        window.clearTimeout(highlightTimerRef.current);
        highlightTimerRef.current = window.setTimeout(() => {
            el.classList.remove('text-danger', 'bg-danger-clarity');
        }, 1600);
    };

    // >>> CHANGE: use toDataIndex() everywhere
    const scrollToError = (rowFromServer, attribute) => {
        const dataIndex = toDataIndex(rowFromServer); // normalized
        const colIndex = headerIndexMap[normalize(attribute)];
        const rowEl = document.getElementById(`row-${dataIndex}`);
        const cellEl = colIndex != null ? document.getElementById(`cell-${dataIndex}-${colIndex}`) : null;
        doHighlight(cellEl || rowEl);
    };

    // >>> CHANGE: for file errors (column-only), scroll to header or first cell
    const scrollToColumn = (attribute) => {
        const colIndex = headerIndexMap[normalize(attribute)];
        if (colIndex == null) return;
        const headerEl = document.getElementById(`header-${colIndex}`);
        // try first data row cell as a fallback visual target
        const firstCellEl = document.getElementById(`cell-1-${colIndex}`);
        doHighlight(headerEl || firstCellEl);
    };

    // >>> CHANGE: normalize fileError into {attribute,message}
    const fileErrorsAsItems = useMemo(() => {
        if (!Array.isArray(fileError)) return [];

        return fileError.map((e) => {
            // if already an object (preferred shape from backend or validator)
            if (typeof e === 'object' && e) {
                const row = e.row ?? null;
                const attr = (e.attribute || e.field || 'unknown').toString();
                const msg = (e.message || 'This field is required').toString();

                return {
                    row,
                    attribute: normalize(attr),
                    message: msg,
                };
            }

            // fallback: string messages
            const s = String(e ?? '').trim();
            const lower = s.toLowerCase();

            let row = null;
            let attribute = '';
            let message = '';

            // try to extract "Row X: Column is required" pattern
            const rowMatch = s.match(/row\s*(\d+)/i);
            console.log('rowMatch', rowMatch);

            if (rowMatch) {
                row = parseInt(rowMatch[1], 10);
            }

            if (s.includes(':')) {
                const [left, ...rest] = s.split(':');
                attribute = left.trim();
                message = rest.join(':').trim();
            } else if (lower.endsWith(' is required') || lower.endsWith(' is required.')) {
                attribute = s.slice(0, lower.indexOf(' is required')).trim();
                message = 'is required';
            } else {
                attribute = s;
                message = 'This field is required';
            }

            const attrNorm = normalize(attribute);

            // remove duplicated attribute prefix in message
            const attrWords = attrNorm.replace(/_/g, ' ');
            if (message.toLowerCase().startsWith(attrWords)) {
                message = message.slice(attrWords.length).replace(/^[:\-]\s*/, '').trim();
                if (!message) message = 'This field is required';
            }
            console.log({ row, attrNorm, message });

            return {
                row,
                attribute: attrNorm,
                message,
            };
        });
    }, [fileError]);


    return (
        <div className='modal-wrapper'>
            <div className={`modal-body max-h-[90vh] ${uploadedFile ? 'container-fixed' : 'w-4/5 max-w-xl'}`}>
                <h3 className="capitalize card-title">{title}</h3>
                <button
                    type="button"
                    onClick={onClose}
                    className="modal-close-btn"
                >
                    <KeenIcon icon="cross" />
                </button>

                <Formik
                    innerRef={formikRef}
                    initialValues={{ mapping_id: null }}
                    onSubmit={(values) => onConfirm(values)}
                >
                    {({ errors, touched, setFieldValue }) => (
                        <Form>
                            {enableExternalImport && (
                                <div className="my-4">
                                    <label htmlFor="mapping_id">Select External Source <span className='text-danger'>*</span></label>
                                    <LazyFormikSelect
                                        name="mapping_id"
                                        placeholder="Select a source"
                                        options={sourceOptions}
                                        isLoading={sourceLoading}
                                        onFocus={() => loadSource(null)}
                                        onChange={async (selectedOption) => {
                                            setSelectedSource('external');
                                            await getSingleSource(selectedOption.value);
                                        }}
                                    />
                                </div>
                            )}
                            {/* Uploader */}
                            {!uploadedFile ? (
                                <div
                                    {...getRootProps()}
                                    className={`border-2 border-dashed my-5 p-5 h-[150px] flex items-center justify-center flex-col text-sm rounded-md cursor-pointer transition ${isDragActive ? 'border-primary bg-primary-light' : 'border-gray-300'
                                        }`}
                                >
                                    <input {...getInputProps()} />
                                    {isDragActive ? <p>Drop the Excel file here ...</p> : <p>Drag & drop your Excel file here, or click to select</p>}
                                    <p className="mt-1 text-xs text-gray-400">Only .xls or .xlsx files are supported</p>
                                    {error && <p className="mt-2 text-xs text-danger">{error}</p>}
                                </div>
                            ) : (
                                <div className="flex items-center justify-between p-4 mt-2 border-2 rounded-md border-primary bg-primary-light">
                                    <p className="text-sm font-medium">{uploadedFile.name}</p>
                                    <button type="button" onClick={removeFile} className="ml-4 text-sm underline text-danger">
                                        Remove
                                    </button>
                                </div>
                            )}

                            {/* >>> CHANGE: File Errors styled like server errors, with jump-to-column */}
                            {uploadedFile && fileErrorsAsItems.length > 0 && (
                                <div className="mt-4 text-sm">
                                    <p className="mb-2 font-semibold text-danger">File Errors:</p>
                                    <div className="max-h-[170px] overflow-auto border rounded-md divide-y divide-light bg-gray-100 mb-1.5">
                                        {fileErrorsAsItems.map((it, idx) => (
                                            <div key={idx} className="px-3 py-1.5">
                                                {it.row ? (
                                                    <div className="flex items-center justify-between">
                                                        <p className="font-semibold text-xs">S# {toDataIndex(it.row)}</p>
                                                        <button
                                                            onClick={() => scrollToError(it.row, it.attribute)}
                                                            className="badge badge-xs badge-primary badge-pill badge-outline gap-1"
                                                            type="button"
                                                        >
                                                            <ArrowDownToLine className="size-2.5" />
                                                            Jump to row
                                                        </button>
                                                    </div>
                                                ) : null}

                                                <div className="flex items-start gap-2 mt-2">
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            it.row
                                                                ? scrollToError(it.row, it.attribute) // row + col
                                                                : scrollToColumn(it.attribute) // header only
                                                        }
                                                        className="shrink-0 badge badge-xs badge-danger badge-outline"
                                                    >
                                                        {it.attribute}
                                                    </button>
                                                    <div className="text-xs leading-relaxed">{it.message}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <Alert variant="info" className="w-fit text-start !p-1.5 !text-xs ml-auto" icon="information">
                                        <div className="!text-xs">Click a field badge to jump to the exact cell.</div>
                                    </Alert>
                                </div>
                            )}


                            {/* Server Errors (grouped) */}
                            {uploadedFile && groupedErrors.length > 0 && (
                                <div className="mt-4 text-sm">
                                    <p className="mb-2 font-semibold text-danger">Import Errors:</p>
                                    <div className="max-h-[170px] overflow-auto border rounded-md divide-y divide-light bg-gray-100 mb-1.5">
                                        {groupedErrors.map((group) => (
                                            <div key={group.rowNumber} className="px-3 py-1.5">
                                                <div className="flex items-center justify-between">
                                                    {/* >>> CHANGE: display normalized S# */}
                                                    <p className="font-semibold text-xs">S# {toDataIndex(group.rowNumber)}</p>
                                                    <button
                                                        onClick={() => scrollToError(group.rowNumber, group.items?.[0]?.attribute)}
                                                        className="badge badge-xs badge-primary badge-pill badge-outline gap-1"
                                                        title="Scroll to row"
                                                        type="button"
                                                    >
                                                        <ArrowDownToLine className='size-2.5' />
                                                        Jump to row
                                                    </button>
                                                </div>

                                                <ul className="mt-2 space-y-2">
                                                    {group.items.map((it, ii) => (
                                                        <li key={ii}>
                                                            <div className="flex items-start gap-2">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => scrollToError(group.rowNumber, it.attribute)}
                                                                    className="shrink-0 badge badge-xs badge-danger badge-outline"
                                                                    title={`Scroll to ${it.attribute}`}
                                                                >
                                                                    {it.attribute}
                                                                </button>
                                                                <div className="text-xs leading-relaxed">
                                                                    {it.errors.map((msg, mi) => (
                                                                        <div key={mi} className="mb-0.5">
                                                                            {msg?.toString().trim() || 'Unknown error'}
                                                                            {it.value != null && (
                                                                                <span className="ml-1 opacity-70">
                                                                                    (value: <code>{String(it.value)}</code>)
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ))}
                                    </div>
                                    <div className='flex items-center justify-between gap-1 flex-wrap'>
                                        {uniqueConfirmation &&
                                            <Alert variant="warning" className="w-fit text-start !p-1.5 !text-xs" icon="question">
                                                <div className="!text-xs">Duplicate rows detected! you can still import the file. click "Proceed Anyway" to import.</div>
                                            </Alert>
                                        }
                                        <Alert variant="info" className="w-fit text-start !p-1.5 !text-xs ml-auto" icon="information">
                                            <div className="!text-xs">Click a field badge to jump to the exact cell.</div>
                                        </Alert>

                                    </div>
                                </div>
                            )}

                            {/* Preview Table */}
                            {fileData.length > 0 && (
                                <div className="mt-3 max-h-[300px] overflow-auto">
                                    <h4 className="text-sm font-medium">Preview File Data</h4>
                                    <table className="w-full mt-2 table-auto">
                                        <thead>
                                            <tr>
                                                <th className="sticky top-0 z-10 w-12 p-2 text-xs text-left bg-gray-100 bor1er">
                                                    S#
                                                </th>
                                                {fileData[0].map((header, index) => (
                                                    <th
                                                        key={index}
                                                        id={`header-${index}`}
                                                        className="border p-2 text-left text-xs min-w-[120px] capitalize sticky top-0 z-10 bg-gray-100"
                                                    >
                                                        {header.replace(/[-_]/g, ' ')}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {fileData.slice(1).map((row, rowIndex) => {
                                                const dataIndex = rowIndex + 1; // first data row is 1
                                                return (
                                                    <tr id={`row-${dataIndex}`} key={rowIndex}>
                                                        <td className="p-2 text-xs text-center border">{rowIndex + 1}</td>
                                                        {row.map((cell, cellIndex) => (
                                                            <td
                                                                id={`cell-${dataIndex}-${cellIndex}`}
                                                                key={cellIndex}
                                                                className="p-2 text-xs border align-top"
                                                            >
                                                                {cell}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            <div className="flex items-center justify-between gap-2 mt-4">
                                {!enableExternalImport &&
                                    <button onClick={handleDownloadSampleFile} disabled={downloadLoading} className="mt-2 btn btn-sm btn-link" type="button">
                                        Download Sample File
                                    </button>
                                }
                                <div className="flex items-center gap-2">
                                    <button
                                        type="submit"
                                        disabled={!uploadedFile || fileError || loading}
                                        className="mt-2 btn btn-sm btn-primary"
                                    >
                                        {loading ? 'Please Wait...' : buttonLabel}
                                    </button>
                                    <button type="button" onClick={onClose} className="mt-2 btn btn-sm btn-outline btn-danger">
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </div >
    );
});

export { DropZoneModal };
